import { TASK_STATUSES, statusLabel } from './taskStatus';

// ─────────────────────────────────────────────────────────────────────────────
// What the four task views share: how rows are grouped, and what a group is.
//
// ⚠️ ONE GROUPING FUNCTION FOR ALL FOUR VIEWS, deliberately. The list's
// headings, the board's columns and the timeline's bands are the same question
// asked three ways — "which pile is this in" — and a board that splits by a
// different rule than the list beside it is a board nobody can reconcile with
// the list. The table is the one view that does not group; it sorts.
//
// ⚠️ EMPTY GROUPS ARE KEPT FOR STATUS AND DROPPED FOR EVERYTHING ELSE, and the
// difference is not an inconsistency. A board needs its "Completed" column to
// exist even when nothing is in it — it is a place to drag a card TO. A client
// with no tasks is not a place anything goes; rendering it would turn the
// client list into the grouping.
// ─────────────────────────────────────────────────────────────────────────────

/** A task's client — primary, else the first linked one. Mirrors the page. */
export const clientOf = (t) => t.clientId || (t.clientIds && t.clientIds[0]) || null;

/** Its compliance type, via instance → enrollment → ComplianceType. */
export const typeOf = (t) => t.complianceInstanceId?.enrollmentId?.complianceTypeId || null;

/** The one person a task is assigned to. See src/utils/taskPeople.js. */
export const assigneeOf = (t) => t.preparerId || null;

export const GROUP_BYS = [
  { value: 'status', label: 'Status' },
  { value: 'client', label: 'Client' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'category', label: 'Category' },
  { value: 'none', label: 'No grouping' },
];

// Initials for an avatar chip. ⚠️ Two words at most: an Indian name routinely
// runs to four, and "RKSN" in a 28px circle is unreadable at any size.
export const initialsOf = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((w) => w[0] || '')
  .join('')
  .toUpperCase() || '—';

const UNASSIGNED = '__none__';

const KEYERS = {
  status: (t) => ({ key: t.status || 'todo', label: statusLabel(t.status) }),
  client: (t) => {
    const c = clientOf(t);
    return { key: c?._id || UNASSIGNED, label: c?.displayName || 'No client' };
  },
  assignee: (t) => {
    const a = assigneeOf(t);
    return { key: a?._id || UNASSIGNED, label: a?.name || 'Unassigned' };
  },
  category: (t) => {
    const ct = typeOf(t);
    return { key: ct?.code || 'OTHER', label: ct?.name || 'Others' };
  },
};

/**
 * Split tasks into ordered groups.
 *
 * @param {Array}  tasks    already filtered and sorted by the page
 * @param {string} groupBy  one of GROUP_BYS
 * @returns {Array<{key, label, tasks}>}
 *
 * ⚠️ THE INCOMING ORDER IS PRESERVED INSIDE EACH GROUP. The page has already
 * sorted — by due date, priority, whatever the user picked — and re-sorting
 * here would silently override a control the user just used.
 */
export function groupTasks(tasks, groupBy) {
  if (groupBy === 'none') {
    return [{ key: 'all', label: 'All tasks', tasks }];
  }

  const keyer = KEYERS[groupBy] || KEYERS.status;
  const buckets = new Map();

  // Status gets its columns up front and in the workflow's own order, so an
  // empty one still exists to drag onto and the order never depends on which
  // statuses happen to be present today.
  if (groupBy === 'status') {
    TASK_STATUSES.forEach((s) => buckets.set(s.value, { key: s.value, label: s.label, tasks: [] }));
  }

  tasks.forEach((t) => {
    const { key, label } = keyer(t);
    if (!buckets.has(key)) buckets.set(key, { key, label, tasks: [] });
    buckets.get(key).tasks.push(t);
  });

  const groups = [...buckets.values()];
  if (groupBy === 'status') return groups;

  // ⚠️ "Unassigned" / "No client" LAST, not alphabetically among the names.
  // It is the pile to clear, not a colleague, and sorting it under N leaves it
  // buried in the middle of the page.
  return groups
    .filter((g) => g.tasks.length)
    .sort((a, b) => {
      if (a.key === UNASSIGNED) return 1;
      if (b.key === UNASSIGNED) return -1;
      return a.label.localeCompare(b.label);
    });
}

export const GROUP_UNASSIGNED_KEY = UNASSIGNED;
